import { useEffect, useState } from 'react';
import { Hint, Screen, SecondaryButton, Title } from './kit';

interface Props {
  /** Шаг задаётся снаружи: письмо отправлено — значит родитель ушёл в почту
   *  и может вернуться на этот экран уже другой вкладкой. */
  step: 'ask' | 'code';
  onSend: (email: string) => void;
  onVerify: (code: string, password: string) => void;
  onBack: () => void;
  /** Секунды до того, как повторная отправка снова разрешена. Пауза стоит
   *  на сервере (не чаще письма в минуту одному адресу), и кнопка обязана
   *  показывать её, а не молча отдавать отказ. */
  cooldown: number;
  busy?: boolean;
  error?: string | null;
  email?: string;
}

const FIELD =
  'min-h-12 w-full rounded-xl border-2 border-line bg-card p-3 text-[1.05rem] text-ink';

/** Восстановление пароля кодом, а не ссылкой.
 *
 *  Три довода, в порядке силы. Родитель заводит запись на планшете ребёнка,
 *  а почту читает на своём телефоне — ссылка авторизует не то устройство, а
 *  в установленном приложении ещё и открывается во встроенном браузере
 *  почты. Отправитель переписывает любой `href` на свой редиректор
 *  (измерено), и письмо «сбросьте пароль» со ссылкой на чужой трекер
 *  выглядит как фишинг ровно там, где доверие письму важнее всего. И
 *  наконец одноразовую ссылку может погасить почтовый сканер.
 *
 *  Ссылки и кода в одном письме быть не может: `{{ .Token }}` и
 *  `{{ .ConfirmationURL }}` — один и тот же секрет, а не два. */
export function RecoveryScreen({
  step, onSend, onVerify, onBack, cooldown, busy, error, email: sentTo,
}: Props) {
  const [email, setEmail] = useState(sentTo ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');

  /** Поле кода получает фокус само: родитель пришёл сюда из почты с
   *  восемью цифрами в голове. */
  useEffect(() => {
    if (step === 'code') document.getElementById('recovery-code')?.focus();
  }, [step]);

  if (step === 'ask') {
    return (
      <Screen>
        <Title>Восстановление пароля</Title>
        <Hint>Пришлём на почту код из восьми цифр. Ссылок в письме не будет.</Hint>
        <form
          className="sentry-mask flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (email.trim() && !busy) onSend(email.trim());
          }}
        >
          <label className="flex flex-col gap-1 text-[0.95rem] text-muted">
            Почта родителя
            <input
              type="email"
              value={email}
              autoComplete="username"
              inputMode="email"
              required
              className={FIELD}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          {/* Отказ относится к форме целиком — сеть, предел писем, — а не к
              полю почты, поэтому немедленное объявление без связи с полем.
              Абзац появляется только вместе с текстом: вставленный `alert`
              браузеры объявляют. */}
          {error && <p role="alert" className="m-0 text-[0.95rem] text-incorrect">{error}</p>}
          <button
            type="submit"
            disabled={email.trim() === '' || busy || cooldown > 0}
            className="rounded-[0.85rem] bg-accent p-4 text-[1.1rem] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#c7cddb]"
          >
            {cooldown > 0 ? `Отправить код (через ${cooldown} с)` : 'Отправить код'}
          </button>
        </form>
        <SecondaryButton bottom onClick={onBack}>
          Назад ко входу
        </SecondaryButton>
      </Screen>
    );
  }

  return (
    <Screen>
      <Title>Код из письма</Title>
      <Hint>
        Отправили код на {sentTo}. Введите его и придумайте новый пароль.
      </Hint>
      {/* Родитель, дважды нажавший «забыл пароль», получает два письма, а
          действует только последний код. Без этой строки отказ выглядит
          как «ссылка недействительна», только цифрами. */}
      <Hint>Если писем несколько, годится код из последнего.</Hint>
      <form
        className="sentry-mask flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (code.trim() && password && !busy) onVerify(code.trim(), password);
        }}
      >
        <label className="flex flex-col gap-1 text-[0.95rem] text-muted">
          Код из письма
          <input
            id="recovery-code"
            type="text"
            value={code}
            /** iOS предлагает подстановку по строке «@mathpaw.com #код»,
             *  которая стоит в самом письме. */
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            /** Не восемь, а с запасом. Длина кода задаётся настройкой
             *  сервера (`otp_length`), и жёсткая восьмёрка здесь означала
             *  бы, что смена настройки молча обрезает код на вводе:
             *  родитель видит в письме верные цифры, вводит их, а поле
             *  отбрасывает последние. Сервер сам скажет, если код не тот. */
            maxLength={12}
            required
            className={FIELD}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
          />
        </label>
        <label className="flex flex-col gap-1 text-[0.95rem] text-muted">
          Новый пароль
          <input
            type="password"
            value={password}
            autoComplete="new-password"
            minLength={8}
            required
            className={FIELD}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        {/* Свой абзац у каждого шага, и роль нужна обоим: роль в одной
            ветке другую не озвучивает. С полем не связан по той же причине —
            «код истёк», «нет связи» относятся к форме, а полей два. */}
        {error && <p role="alert" className="m-0 text-[0.95rem] text-incorrect">{error}</p>}
        <button
          type="submit"
          disabled={code.trim() === '' || password === '' || busy}
          className="rounded-[0.85rem] bg-accent p-4 text-[1.1rem] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#c7cddb]"
        >
          Сменить пароль
        </button>
      </form>
      <button
        type="button"
        disabled={cooldown > 0 || busy}
        onClick={() => onSend(sentTo ?? email)}
        className="min-h-11 rounded-lg text-[0.95rem] text-muted underline disabled:no-underline disabled:opacity-60"
      >
        {cooldown > 0 ? `Прислать новый код можно через ${cooldown} с` : 'Прислать новый код'}
      </button>
      <SecondaryButton bottom onClick={onBack}>
        Назад ко входу
      </SecondaryButton>
    </Screen>
  );
}
